<template>
  <div class="richmenu-dashboard page-container ma-0">
    <div class="sticker-pack__header d-flex align-center">
      <h1 class="text-h1 neutral-041--text">
        {{ $t('MENU.CUSTOMER_JOURNEY') }}
      </h1>

      <Input
        v-model="search"
        class="ml-auto mr-3"
        :placeholder="$t('SEARCH')"
        hide-details
        background-color="neutral-cold-058"
      >
        <template v-slot:prepend-inner>
          <v-icon
            color="neutral-042"
            size="18"
          >
            $vuetify.icons.searchIcon
          </v-icon>
        </template>
      </Input>
      <v-btn
        class="text-body-2"
        height="32"
        dark
        depressed
        color="primary-001"
        :to="{name: 'createCustomerJourney'}"
      >
        <v-icon
          size="16"
          class="mr-1"
        >
          mdi-plus
        </v-icon>
        {{ $t('CUSTOMER_JOURNEY.ADD_JOURNEY') }}
      </v-btn>
    </div>

    <v-data-table
      class="mt-5 custom-data-table sticker-pack__table table-click-redirect"
      :headers="headers"
      :items="journeyList"
      :search="search"
      :loading="isLoadingTable"
      loading-text="Loading..."
      @click:row="goToJourneyPage"
    >
      <template v-slot:item.name="{ item }">
        <span class="text-body-3 neutral-041--text">{{ item.name }}</span>
      </template>
      <template v-slot:item.triggerEventType="{ item }">
        <span class="text-body-3 neutral-041--text"> {{ getTriggerEventText(item.triggerEventType) }}</span>
      </template>
      <template
        v-slot:item.activeTime="{ item }"
      >
        <td class="text-body-3 neutral-041--text">
          {{ formatEnableTime(item.activeTime.startAt, item.activeTime.endAt) }}
        </td>
      </template>
      <template v-slot:item.status="{ item }">
        <v-chip
          dark
          class="status-chip"
          :color="getStatusStyle(item.status)"
          :class="getStatusStyle(item.status)"
        >
          {{ getStatusText(item.status) }}
        </v-chip>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              dark
              icon
              tile
              color="primary-001"
              v-bind="attrs"
              v-on="on"
              class="data-table-action-btn"
            >
              <v-icon color="primary-001">
                mdi-dots-horizontal
              </v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              v-if="item.status !== 'drafting'"
              link
              @click="goToJourneyPage(item)"
            >
              <v-list-item-content>
                {{ $t('CUSTOMER_JOURNEY.VIEW_JOURNEY') }}
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              v-show="item.status === 'drafting'"
              link
              @click="goToJourneyPage(item)"
            >
              <v-list-item-content>
                {{ $t('EDIT') }}
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              v-show="item.status === 'processing'"
              link
              @click="openConfirmModal(item, 'pause')"
            >
              <v-list-item-content>
                {{ $t('CUSTOMER_JOURNEY.DISABLE_TRIGGER') }}
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              v-show="showStopJourneyBtn(item.status)"
              link
              @click="openConfirmModal(item, 'stop')"
            >
              <v-list-item-content>
                {{ $t('CUSTOMER_JOURNEY.STOP_JOURNEY') }}
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              link
              @click="openConfirmModal(item, 'clone')"
            >
              <v-list-item-content>
                {{ $t('DUPLICATE') }}
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              v-show="showDeleteJourneyBtn(item.status)"
              link
              @click="openConfirmModal(item, 'delete')"
            >
              <v-list-item-content>
                {{ $t('DELETE') }}
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
      <!-- Footer -->
      <template v-slot:footer.page-text="props">
        {{ props.pageStart }} - {{ props.pageStop }} of
        {{ props.itemsLength }}
      </template>

      <template v-slot:no-results>
        <v-img
          class="mx-auto mt-192 mb-3"
          :src="require(`@/assets/images/general/icon_no-results.svg`)"
          width="39"
          contain
          height="51"
        />
        <p class="text-body-3 neutral-043--text mb-192">
          {{ $t('CUSTOMER_JOURNEY.NO_EXIST_CUSTOMER_JOURNEY') }}
        </p>
      </template>
      <template v-slot:no-data>
        <v-img
          class="mx-auto mt-192 mb-3"
          :src="require(`@/assets/images/general/icon_no-results.svg`)"
          width="39"
          contain
          height="51"
        />
        <p class="text-body-3 neutral-043--text mb-192">
          {{ $t('CUSTOMER_JOURNEY.NO_EXIST_CUSTOMER_JOURNEY') }}
        </p>
      </template>
    </v-data-table>
    <Modal
      ref="confirmModal"
    >
      <template v-slot:actions>
        <v-card-actions class="pa-0 pt-8">
          <v-spacer />
          <v-btn
            color="neutral-045"
            outlined
            @click="$refs.confirmModal.closeModal();"
            class="text-body-2 cancel-btn"
          >
            {{ $t('CANCEL') }}
          </v-btn>
          <v-btn
            :loading="isBtnClickLoading"
            :color="currentActionType === 'clone' ? 'primary-001' : 'danger-022'"
            dark
            :outlined="currentActionType !== 'clone'"
            depressed
            @click="confirmAction"
            class="text-body-2 ml-3"
          >
            {{ confirmBtnText }}
          </v-btn>
        </v-card-actions>
      </template>
    </Modal>

    <Notification
      ref="notification"
    />
  </div>
</template>
<script>
import { mapState } from 'vuex';
import Input from '@/components/form/Input.vue';
import Modal from '@/components/Modal.vue';
import { FEATURE } from '@/utils/constants';
import customerJourneyApi from '../../restapis/customerJourney';

const JOURNEY_STATUS = {
  DRAFTING: 'drafting', // 草稿
  SCHEDULING: 'scheduling', // 排程中
  PROCESSING: 'processing', // 進行中
  PAUSED: 'paused', // 停止新觸發 但Process要跑完
  STOPPING: 'stopping', // 停止新觸發 中斷所有Process
  STOPPED: 'stopped' // 停止新觸發 所有Process已中斷
};
export default {
  name: 'CustomerJourneyDashboard',
  metaInfo: {
    title: 'Customer Journey Dashboard - Omnichat Admin Panel'
  },
  components: {
    Input,
    Modal

  },
  data() {
    return {
      omoBindingSettingUrl: `${process.env.VUE_APP_CHAT_ADMIN_URL}/omo-binding-setting.html`,
      search: '',
      journeyList: [],
      isLoadingTable: false,
      currentJourneyId: null,
      isBtnClickLoading: false,
      currentActionType: null,
      confirmBtnText: ''
    };
  },
  computed: {
    ...mapState({
      currentUserDict: (state) => state.currentUserDict
    }),
    headers() {
      return [
        {
          text: this.$t('NAME'),
          sortable: true,
          value: 'name',
          width: '30%',
          class: 'text-body-2 neutral-042--text'
        },
        {
          text: this.$t('TRIGGER_TYPE'),
          sortable: true,
          value: 'triggerEventType',
          width: '15%',
          class: 'text-body-2 neutral-042--text'
        },
        {
          text: this.$t('START_TIME'),
          sortable: true,
          value: 'activeTime',
          width: '30%',
          class: 'text-body-2 neutral-042--text'
        },
        {
          text: this.$t('STATUS'),
          sortable: true,
          value: 'status',
          width: '15%',
          class: 'text-body-2 neutral-042--text'
        },
        {
          text: this.$t('ACTION'),
          value: 'actions',
          sortable: false,
          width: '10%',
          class: 'text-body-2 neutral-042--text'
        }

      ];
    }
  },
  async mounted() {
    this.$store.commit('loader/showLoader', false);
    await this.fetchJourneyList();
  },
  methods: {
    goToJourneyPage(item) {
      if (item.status === 'drafting') {
        this.$router.push({ name: 'editCustomerJourney', params: { id: item.id } });
      } else {
        this.$router.push({ name: 'previewCustomerJourney', params: { id: item.id } });
      }
    },
    confirmAction() {
      switch (this.currentActionType) {
        case 'clone':
          this.cloneJourney();
          break;
        case 'delete':
          this.deleteJourney();
          break;
        case 'pause':
          this.updateJourneyStatus('pause');
          break;
        case 'stop':
          this.updateJourneyStatus('stop');
          break;
        default:
      }
    },

    async fetchJourneyList() {
      this.isLoadingTable = true;
      try {
        const { data } = await customerJourneyApi.getCustomerJourneyList();
        this.journeyList = data.content;
        this.isLoadingTable = false;
      } catch (error) {
        this.isLoadingTable = false;
        const { name, message } = error.response.data;
        this.$refs.notification.openNotification({
          title: this.$t('CUSTOMER_JOURNEY.FAILED_TO_GET_CUSTOMER_JOURNEY_LIST'),
          content: `${name}-${message}`,
          status: 'error'
        });
      }
    },
    async cloneJourney() {
      this.isBtnClickLoading = true;
      try {
        const { data } = await customerJourneyApi.cloneCustomerJourney(this.currentJourneyId);
        this.$refs.notification.openNotification({
          title: this.$t('CUSTOMER_JOURNEY.COPY_CUSTOMER_JOURNEY_SUCCESSFULLY'),
          status: 'success'
        });
        this.fetchJourneyList();
      } catch (error) {
        const { name, message } = error.response.data;
        this.$refs.notification.openNotification({
          title: this.$t('CUSTOMER_JOURNEY.FAIL_TO_COPY_CUSTOMER_JOURNEY'),
          content: `${name}-${message}`,
          status: 'error'
        });
      } finally {
        this.$refs.confirmModal.closeModal();
        this.isBtnClickLoading = false;
      }
    },
    async deleteJourney() {
      this.isBtnClickLoading = true;
      try {
        const { data } = await customerJourneyApi.deleteCustomerJourney(this.currentJourneyId);
        this.journeyList = this.journeyList.filter((item) => item.id !== this.currentJourneyId);

        this.$refs.notification.openNotification({
          title: this.$t('NOTIFICATION.DELETE_SUCCESS'),
          status: 'success'
        });
      } catch (error) {
        const { name, message } = error.response.data;
        this.$refs.notification.openNotification({
          title: this.$t('ERROR_MSG.DELETE_FAIL'),
          content: `${name}-${message}`,
          status: 'error'
        });
      } finally {
        this.$refs.confirmModal.closeModal();
        this.isBtnClickLoading = false;
      }
    },
    async updateJourneyStatus(mode) {
      this.isBtnClickLoading = true;
      try {
        const { data } = await customerJourneyApi.updateCustomerJourney(this.currentJourneyId, mode);
        this.$refs.notification.openNotification({
          title: this.$t('NOTIFICATION.UPDATE_SUCCESS'),
          status: 'success'
        });
        this.fetchJourneyList();
      } catch (error) {
        const { name, message } = error.response.data;
        this.$refs.notification.openNotification({
          title: this.$t('ERROR_MSG.UPDATE_FAIL'),
          content: `${name}-${message}`,
          status: 'error'
        });
      } finally {
        this.$refs.confirmModal.closeModal();
        this.isBtnClickLoading = false;
      }
    },
    async stopJourney() {
      this.isBtnClickLoading = true;
    },
    getStatusText(status) {
      switch (status) {
        case JOURNEY_STATUS.DRAFTING:
          return this.$t('STATUS_LIST.DRAFT');
        case JOURNEY_STATUS.SCHEDULING:
          return this.$t('STATUS_LIST.SCHEDULED');
        case JOURNEY_STATUS.PROCESSING:
          return this.$t('STATUS_LIST.IN_PROGRESS');
        case JOURNEY_STATUS.PAUSED:
          return this.$t('CUSTOMER_JOURNEY.TRIGGER_DISABLED');
        case JOURNEY_STATUS.STOPPING:
          return this.$t('CUSTOMER_JOURNEY.STOPPING');
        case JOURNEY_STATUS.STOPPED:
          return this.$t('CUSTOMER_JOURNEY.STOPPED');
        default:
          return this.$t('CUSTOMER_JOURNEY.STOPPED');
      }
    },
    getStatusStyle(status) {
      switch (status) {
        case JOURNEY_STATUS.DRAFTING:
          return 'draft-status';
        case JOURNEY_STATUS.SCHEDULING:
          return 'scheduled-status';
        case JOURNEY_STATUS.PROCESSING:
          return 'processing-status';
        case JOURNEY_STATUS.PAUSED:
          return 'paused-status';
        case JOURNEY_STATUS.STOPPING:
          return 'stopping-status';
        case JOURNEY_STATUS.STOPPED:
          return 'stopped-status';
        default:
          return 'stopped-status';
      }
    },
    showStopJourneyBtn(status) {
      const allowStopStatus = [JOURNEY_STATUS.PROCESSING, JOURNEY_STATUS.SCHEDULING, JOURNEY_STATUS.PAUSED];
      return allowStopStatus.includes(status);
    },
    showDeleteJourneyBtn(status) {
      const allowDeleteStatus = [JOURNEY_STATUS.DRAFTING, JOURNEY_STATUS.SCHEDULING, JOURNEY_STATUS.STOPPED];
      return allowDeleteStatus.includes(status);
    },
    getTriggerEventText(triggerEventType) {
      switch (triggerEventType) {
        case ('customized_attribute'):
          return this.$t('CUSTOMER_JOURNEY.CUSTOMIZED_ATTRIBUTE');
        case ('append_tag'):
          return this.$t('CUSTOMER_JOURNEY.APPEND_TAG');
        case ('complete_checkout'):
          return this.$t('CUSTOMER_JOURNEY.COMPLETE_PURCHASE');
        case ('add_channel_user'):
          return this.$t('CUSTOMER_JOURNEY.ADD_CONTACTS');
        default:
          return this.$t('CUSTOMER_JOURNEY.SYSTEM_FEATURES');
      }
    },
    formatEnableTime(startAt, endAt) {
      if (!startAt && !endAt) {
        return this.$t('ENABLE_PERMANENTLY');
      }
      return `${this.dayjs(startAt).format('YYYY/MM/DD HH:mm')} ~ ${this.dayjs(endAt).format('YYYY/MM/DD HH:mm')}`;
    },

    openConfirmModal(item, type) {
      this.currentJourneyId = item.id;
      this.currentActionType = type;
      let title = '';
      let content = '';
      switch (type) {
        case 'clone':
          title = this.$t('CUSTOMER_JOURNEY.DUPLICATED_CONFIRMATION');
          this.confirmBtnText = this.$t('DUPLICATE');
          break;
        case 'delete':
          title = this.$t('CUSTOMER_JOURNEY.DELETE_CONFIRMATION');
          this.confirmBtnText = this.$t('DELETE');
          break;
        case 'pause':
          title = this.$t('CUSTOMER_JOURNEY.DISABLE_TRIGGER_CONFIRMATION');
          content = this.$t('CUSTOMER_JOURNEY.ACTION_AFTER_DISABLE_TRIGGER_DESCRIPTION');
          this.confirmBtnText = this.$t('CUSTOMER_JOURNEY.DISABLE_TRIGGER');
          break;
        case 'stop':
          title = this.$t('CUSTOMER_JOURNEY.STOP_TRIGGER_CONFIRMATION');
          content = this.$t('CUSTOMER_JOURNEY.AFTER_STOPPING_THE_JOURNEY_DESCRIPTION');
          this.confirmBtnText = this.$t('CUSTOMER_JOURNEY.STOP_JOURNEY');
          break;
        default:
      }
      this.$refs.confirmModal.openModal(title, content);
    }
  }
};
</script>

<style lang="scss" scoped>
.mt-192 {
  margin-top: 192px;
}

.mb-192 {
  margin-bottom: 192px;
}
::v-deep.status-chip {
  &.v-chip.v-size--default {
    max-height: 25px;
    padding: 0 8px;
  }
  &.v-chip .v-chip__content {
    font-size: 12px;
    line-height: 16px;
    font-weight: 400;
  }
  &.draft-status {
    color: $warning-030;
    caret-color: $warning-030;
    background-color: $warning-034;
    border-color: $warning-034;
  }
  &.scheduled-status {
    color: $primary-000;
    caret-color: $primary-000;
    background-color: $primary-004;
    border-color: $primary-004;
  }
  &.processing-status {
    color: $secondary-013;
    caret-color: $secondary-013;
    background-color: $secondary-012;
    border-color: $secondary-012;
  }
  &.paused-status {
    color: $purple-061;
    caret-color: $purple-061;
    background-color: $purple-067;
    border-color: $purple-067;
  }
  &.stopping-status {
    color: $danger-020;
    caret-color: $danger-020;
    background-color: $danger-024;
    border-color: $danger-024;
  }
  &.stopped-status {
    color: $neutral-cold-050;
    caret-color: $neutral-cold-050;
    background-color: $neutral-045;
    border-color: $neutral-045;
  }
}
</style>
